import { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavButton } from '../../hooks/useNavButton';
import SEOHead from '../../components/SEOHead';
import HeroSection from './components/HeroSection';

// Lazy-load below-the-fold sections to reduce initial bundle
const HowItWorksSection = lazy(() => import('./components/HowItWorksSection'));
const LaylaSection = lazy(() => import('./components/LaylaSection'));
const PlansSection = lazy(() => import('./components/PlansSection'));
const FeaturesSection = lazy(() => import('./components/FeaturesSection'));
const TestimonialsSection = lazy(() => import('./components/TestimonialsSection'));
const CtaSection = lazy(() => import('./components/CtaSection'));
const HomeFooter = lazy(() => import('./components/HomeFooter'));

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

const homeJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'EntreNós',
    url: SITE_URL,
    description:
      'Plataforma de conexão para casais com missões personalizadas por IA, gamificação e ferramentas de bem-estar emocional.',
    inLanguage: 'pt-BR',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'EntreNós',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/1b0e966215e432d351b2e9c11ca0b10e/154dcc8a65fed03ee4761cc080c3eddf.png',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'suporteentrenos@outlook.com',
      contactType: 'customer support',
      availableLanguage: 'Portuguese',
    },
    sameAs: [],
    foundingLocation: {
      '@type': 'Country',
      name: 'Brasil',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'EntreNós',
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'Web',
    inLanguage: 'pt-BR',
    description:
      'Fortaleça seu relacionamento com missões semanais personalizadas por IA, experiências interativas, conquistas e a consultora Layla — especialista em casais.',
    url: SITE_URL,
    offers: [
      {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'BRL',
        name: 'Plano Gratuito',
        description: 'Acesso às funcionalidades básicas sem custo.',
      },
      {
        '@type': 'Offer',
        name: 'Plano Premium',
        priceCurrency: 'BRL',
        description: 'Acesso completo a todos os recursos da plataforma.',
      },
    ],
  },
];

export default function Home() {
  const { user, loading } = useAuth();
  const { navigateTo, isLoading } = useNavButton();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigateTo('/dashboard', 'auto-redirect');
    }
  }, [user, loading]);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 40);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-rose-50 to-amber-50">
      <SEOHead
        title="EntreNós — Plataforma de Conexão para Casais |"
        description="EntreNós fortalece relacionamentos com missões semanais personalizadas por IA, gamificação, conquistas e a consultora Layla especializada em psicologia de casais. Comece gratuitamente."
        keywords="plataforma para casais, missões de relacionamento, IA para casais, fortalecer relacionamento, gamificação relacionamento, Layla IA, conexão emocional casal"
        canonical="/"
        ogType="website"
        schemaJsonLd={homeJsonLd}
        dateModified="2026-04-09"
      />

      {/* ── HEADER ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <img
              src="https://static.readdy.ai/image/1b0e966215e432d351b2e9c11ca0b10e/154dcc8a65fed03ee4761cc080c3eddf.png"
              alt="EntreNós"
              fetchPriority="high"
              decoding="async"
              width="120"
              height="40"
              className="h-10 w-auto"
            />
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigateTo('/login', 'login')}
                disabled={isLoading('login')}
                className="px-5 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading('login') ? (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-loader-4-line animate-spin" /> Entrando...
                  </span>
                ) : (
                  'Entrar'
                )}
              </button>
              <button
                onClick={() => navigateTo('/cadastro', 'cadastro')}
                disabled={isLoading('cadastro')}
                className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-rose-500 to-pink-500 rounded-full hover:from-rose-600 hover:to-pink-600 transition-all whitespace-nowrap shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {isLoading('cadastro') ? (
                  <><i className="ri-loader-4-line animate-spin" /> Aguarde...</>
                ) : (
                  'Começar Agora'
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Banner Beta */}
      <div className="w-full bg-gradient-to-r from-rose-500 via-pink-500 to-rose-400 text-white text-center py-2.5 px-4 text-sm font-medium tracking-wide mt-16">
        <span className="inline-flex items-center gap-2">
          <span className="bg-white/25 text-white text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-widest">Beta</span>
          Estamos em fase Beta — sua experiência nos ajuda a melhorar! Obrigado por fazer parte desta jornada.
        </span>
      </div>

      <HeroSection navigateTo={navigateTo} isLoading={isLoading} />

      <Suspense fallback={<div className="h-20" />}>
        <HowItWorksSection />
        <LaylaSection navigateTo={navigateTo} isLoading={isLoading} />
        <PlansSection navigateTo={navigateTo} isLoading={isLoading} />
        <FeaturesSection />
        <TestimonialsSection />
        <CtaSection navigateTo={navigateTo} isLoading={isLoading} />
        <HomeFooter />
      </Suspense>
    </div>
  );
}
